/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useMemo, useRef } from 'react';
import {
  FlatList,
  Platform,
  RefreshControl,
  View,
  type ListRenderItem,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { colors } from '../../../shared/theme/colors'
import { webOperationalScrollBindings } from '../../../shared/theme/webScrollbar';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import { PosHeaderSection } from './PosHeaderSection';
import { PosProductGridItem } from './PosProductGridItem';
import { PosPackageGridItem } from './PosPackageGridItem';
import { VIRTUALIZED_LIST_PROPS } from '../../../shared/ui/virtualizedListProps';
import { operationalTabListHostStyle, operationalTabListScrollerStyle } from '../../../shared/layout/operationalTabListHost';
import { styles } from '../posShared.styles';
import type { ProductSearchRow } from '../../../catalog/domain-api';
import type { PosCatalogRow, PosPackageGridRow, PosStockSalePolicy } from '../../domain/posTypes';
import { isPosPackageRow } from '../../domain/posTypes';
import { usePosCatalogContextOptional } from '../contexts/PosCatalogContext';

type PosProductCatalogListProps = {
  productListRef?: React.RefObject<FlatList<PosCatalogRow> | null>;
  numColumns: number;
  products?: PosCatalogRow[];
  refreshing?: boolean;
  onRefresh?: () => void;
  contentContainerStyle?: StyleProp<ViewStyle>;
  listHeader?: React.ComponentProps<typeof PosHeaderSection> | null;
  listEmpty?: React.ComponentProps<typeof ModuleEmptyState>;
  paginationDock?: React.ReactNode;
  mobilePagination?: React.ReactNode;
  canEdit?: boolean;
  onProductPress?: (product: ProductSearchRow) => void;
  onPackagePress?: (row: PosPackageGridRow) => void;
  stockSalePolicy?: PosStockSalePolicy;
  addedFlashProductId?: string | null;
  onToggleProductFavorite?: (product: ProductSearchRow) => void;
  isProductFavorite?: (productId: string) => boolean;
  productBadges?: (productId: string) => { combo?: boolean; promo?: boolean };
  onCatalogListViewportHeight?: (height: number) => void;
  onCatalogListHeaderHeight?: (height: number) => void;
};

function PosProductCatalogListInner({
  numColumns,
  ...props
}: PosProductCatalogListProps) {
  const ctx = usePosCatalogContextOptional();
  const fallbackProductListRef = useRef<FlatList<PosCatalogRow> | null>(null);
  const merged = {
    ...(ctx ?? {}),
    ...props,
  };
  const productListRef = merged.productListRef ?? fallbackProductListRef;
  const products = merged.products ?? [];
  const refreshing = merged.refreshing ?? false;
  const onRefresh = merged.onRefresh ?? (() => {});
  const contentContainerStyle = merged.contentContainerStyle;
  const listHeader = merged.listHeader ?? null;
  const listEmpty = merged.listEmpty ?? ({} as React.ComponentProps<typeof ModuleEmptyState>);
  const paginationDock = merged.paginationDock ?? null;
  const mobilePagination = merged.mobilePagination ?? null;
  const canEdit = merged.canEdit ?? false;
  const onProductPress = merged.onProductPress ?? (() => {});
  const onPackagePress = merged.onPackagePress ?? (() => {});
  const stockSalePolicy = merged.stockSalePolicy ?? 'block';
  const addedFlashProductId = merged.addedFlashProductId ?? null;
  const onToggleProductFavorite = merged.onToggleProductFavorite;
  const isProductFavorite = merged.isProductFavorite;
  const productBadges = merged.productBadges;
  const onCatalogListViewportHeight = merged.onCatalogListViewportHeight;
  const onCatalogListHeaderHeight = merged.onCatalogListHeaderHeight;
  const { layoutWide } = useTabWorkspaceLayout();
  const showPullRefresh = !(Platform.OS === 'web' && layoutWide);
  const keyExtractor = useCallback(
    (item: PosCatalogRow) => (isPosPackageRow(item) ? `pkg-${item.packageId}` : item.id),
    [],
  );

  const renderItem: ListRenderItem<PosCatalogRow> = useCallback(
    ({ item }) =>
      isPosPackageRow(item) ? (
        <PosPackageGridItem
          row={item}
          canEdit={canEdit}
          onPress={onPackagePress}
          addedFlash={addedFlashProductId === item.packageId}
        />
      ) : (
        <PosProductGridItem
          product={item}
          canEdit={canEdit}
          onPress={onProductPress}
          onToggleFavorite={onToggleProductFavorite}
          isFavorite={isProductFavorite?.(item.id)}
          badges={productBadges?.(item.id)}
          stockSalePolicy={stockSalePolicy}
          addedFlash={addedFlashProductId === item.id}
        />
      ),
    [
      addedFlashProductId,
      canEdit,
      isProductFavorite,
      onPackagePress,
      onToggleProductFavorite,
      onProductPress,
      productBadges,
      stockSalePolicy,
    ]
  );

  const ListHeaderComponent = useMemo(() => {
    if (!listHeader) return undefined;
    const headerProps = listHeader;
    return () => (
      <View
        onLayout={(e) => {
          if (!layoutWide) return;
          onCatalogListHeaderHeight?.(e.nativeEvent.layout.height);
        }}
        collapsable={false}
      >
        <PosHeaderSection {...headerProps} />
      </View>
    );
  }, [layoutWide, listHeader, onCatalogListHeaderHeight]);

  const ListEmptyComponent = useCallback(
    () => (
      <View style={styles.productListEmptyFill}>
        <ModuleEmptyState {...listEmpty} />
      </View>
    ),
    [listEmpty]
  );

  const ListFooterComponent = useCallback(
    () => mobilePagination ?? null,
    [mobilePagination],
  );

  return (
    <View
      style={[
        operationalTabListHostStyle.host,
        styles.posCatalogListHost,
        layoutWide && styles.posCatalogListHostDesk,
      ]}
    >
      <FlatList
        ref={productListRef}
        key={`product-grid-${numColumns}`}
        {...webOperationalScrollBindings(
          operationalTabListScrollerStyle([styles.productList, layoutWide && styles.productListDesk]),
        )}
        data={products}
        keyExtractor={keyExtractor}
        numColumns={numColumns}
        columnWrapperStyle={
          numColumns > 1
            ? [styles.productGridRow, layoutWide && styles.productGridRowDesk]
            : undefined
        }
        onLayout={(e) => {
          if (!layoutWide) return;
          onCatalogListViewportHeight?.(e.nativeEvent.layout.height);
        }}
        refreshControl={
          showPullRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.textSecondary]}
              tintColor={colors.textSecondary}
            />
          ) : undefined
        }
        contentContainerStyle={contentContainerStyle}
        ListHeaderComponent={ListHeaderComponent}
        ListEmptyComponent={ListEmptyComponent}
        ListFooterComponent={ListFooterComponent}
        renderItem={renderItem}
        alwaysBounceVertical={false}
        overScrollMode="never"
        {...VIRTUALIZED_LIST_PROPS}
      />
      {paginationDock ? <View style={styles.posCatalogPaginationDock}>{paginationDock}</View> : null}
    </View>
  );
}

export const PosProductCatalogList = React.memo(PosProductCatalogListInner);
